import { useMemo, useState } from 'react';
import { FilePlus2, LayoutTemplate, Loader2, Search } from 'lucide-react';
import { useParams } from 'react-router-dom';
import { Button } from '@/shared/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/shared/ui/dialog';
import { Input } from '@/shared/ui/input';
import { Badge } from '@/shared/ui/badge';
import { Label } from '@/shared/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/ui/select';
import { useChecklistTemplates, useChecklistMutations } from '../hooks';
import { useAreaTagsByProject } from '@/features/area-tags/hooks/useAreaTags';
import type { ChecklistTemplate } from '../types';

interface PunchListCreateDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreateFromScratch: (areaId: number | null) => void;
  onCreatedFromTemplate: (newInstanceId: string) => void;
}

const PunchListCreateDialog = ({
  open,
  onOpenChange,
  onCreateFromScratch,
  onCreatedFromTemplate,
}: PunchListCreateDialogProps) => {
  const [search, setSearch] = useState('');
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>('');
  const [selectedAreaValue, setSelectedAreaValue] = useState<string>('general');
  const { projectId } = useParams<{ projectId: string }>();
  const { data: templates, isLoading } = useChecklistTemplates(open);
  const { data: areaTags = [], isLoading: isLoadingAreas } = useAreaTagsByProject(projectId ?? '', {
    enabled: open && !!projectId,
  });
  const { createInstance } = useChecklistMutations();

  const filteredTemplates = useMemo(() => {
    if (!templates) return [];
    if (!search.trim()) return templates;
    const needle = search.toLowerCase().trim();
    return templates.filter(
      (template) =>
        template.title.toLowerCase().includes(needle) ||
        (template.description || '').toLowerCase().includes(needle)
    );
  }, [templates, search]);

  const selectedTemplate = filteredTemplates.find((template) => template.id === selectedTemplateId);
  const selectedAreaId = selectedAreaValue === 'general' ? null : Number(selectedAreaValue);

  const handleCreateFromTemplate = async () => {
    if (!selectedTemplate) return;
    const created = await createInstance.mutateAsync({
      templateId: selectedTemplate.id,
      title: `${selectedTemplate.title} - ${new Date().toLocaleDateString()}`,
      area: selectedAreaId,
    });
    handleOpenChange(false);
    onCreatedFromTemplate(created.id);
  };

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) {
      setSearch('');
      setSelectedTemplateId('');
      setSelectedAreaValue('general');
    }
    onOpenChange(nextOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-2xl bg-white">
        <DialogHeader>
          <DialogTitle>Create New Punch List</DialogTitle>
          <DialogDescription>
            Start from scratch or create an instance from a saved punch list template.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Area Tag (optional)</Label>
            <Select value={selectedAreaValue} onValueChange={setSelectedAreaValue}>
              <SelectTrigger>
                <SelectValue placeholder="Select an area tag" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="general">General (no area tag)</SelectItem>
                {areaTags.map((areaTag) => (
                  <SelectItem key={areaTag.id} value={String(areaTag.id)}>
                    {areaTag.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {isLoadingAreas && (
              <p className="text-xs text-muted-foreground">Loading available area tags...</p>
            )}
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <Button
              variant="outline"
              className="h-auto justify-start p-4"
              onClick={() => {
                handleOpenChange(false);
                onCreateFromScratch(selectedAreaId);
              }}
            >
              <div className="flex items-start gap-3 text-left">
                <FilePlus2 className="mt-0.5 h-5 w-5" />
                <div>
                  <div className="font-medium">Create From Scratch</div>
                  <div className="text-xs text-muted-foreground">
                    Build a new punch list instance with custom sections and items.
                  </div>
                </div>
              </div>
            </Button>

            <div className="rounded-md border p-4">
              <div className="flex items-start gap-3 text-left">
                <LayoutTemplate className="mt-0.5 h-5 w-5 text-muted-foreground" />
                <div>
                  <div className="font-medium">Create From Template</div>
                  <div className="text-xs text-muted-foreground">
                    Select a saved template to create a new punch list instance.
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search templates..."
              className="pl-9"
            />
          </div>

          <div className="max-h-72 space-y-2 overflow-auto rounded-md border p-2">
            {isLoading ? (
              <div className="flex items-center justify-center py-8 text-sm text-muted-foreground">
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Loading templates...
              </div>
            ) : filteredTemplates.length === 0 ? (
              <div className="py-8 text-center text-sm text-muted-foreground">
                No templates available for this project.
              </div>
            ) : (
              filteredTemplates.map((template: ChecklistTemplate) => (
                <button
                  key={template.id}
                  type="button"
                  onClick={() => setSelectedTemplateId(template.id)}
                  className={`w-full rounded-md border p-3 text-left transition-colors ${
                    selectedTemplateId === template.id
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:bg-gray-50'
                  }`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="font-medium">{template.title}</div>
                      <div className="text-xs text-muted-foreground">
                        {template.description || 'No description'}
                      </div>
                    </div>
                    <Badge variant="outline">{template.sections.length} sections</Badge>
                  </div>
                </button>
              ))
            )}
          </div>

          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => handleOpenChange(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleCreateFromTemplate}
              disabled={!selectedTemplate || createInstance.isPending}
            >
              {createInstance.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Create From Template
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PunchListCreateDialog;
